// For Shared Links Tap
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase-config';
import NewsCard from '../components/newsCard';
import './SinglePostPage.css';
import { Capacitor } from '@capacitor/core'; // Import Capacitor

// Helper function to convert Client SDK Timestamp to the JSON/REST API structure expected by NewsCard
const normalizeTimestampForNewsCard = (timestamp) => {
    if (!timestamp) return null;
    
    // If it's a standard Firestore Client SDK Timestamp object
    if (typeof timestamp.toDate === 'function') {
        return {
            _seconds: timestamp.seconds,
            _nanoseconds: timestamp.nanoseconds
        };
    }
    
    // If it's already an object but uses standard seconds instead of _seconds
    if (typeof timestamp === 'object' && typeof timestamp.seconds === 'number' && typeof timestamp._seconds === 'undefined') {
        return {
            _seconds: timestamp.seconds,
            _nanoseconds: timestamp.nanoseconds || 0
        };
    }
    
    return timestamp;
};

const SinglePostPage = ({ setActiveCommentPostId }) => {
    const { postId } = useParams();
    const navigate = useNavigate();
    const [newsItem, setNewsItem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isApp, setIsApp] = useState(false); // New state to detect app

    useEffect(() => {
        setIsApp(Capacitor.isNativePlatform()); // Check if running in native app
        // Or a more robust way:
        // setIsApp(window.location.protocol === 'capacitor:');
    }, []);

    useEffect(() => {
        const fetchPost = async () => {
            if (!postId) {
                setError('No post ID provided in URL.');
                setLoading(false);
                return;
            }

            try {
                const docRef = doc(db, 'userNews', postId);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    const data = docSnap.data();
                    
                    // Normalize the timestamp before setting it to the newsItem state
                    if (data.timestamp) {
                        data.timestamp = normalizeTimestampForNewsCard(data.timestamp);
                    }

                    setNewsItem({ id: docSnap.id, ...data });
                } else {
                    setError('This post could not be found.');
                }
            } catch (err) {
                console.error("Error fetching single post:", err);
                setError('Failed to load the post.');
            } finally {
                setLoading(false);
            }
        };

        fetchPost();
    }, [postId]);

    // NEW: Smart back handler that falls back to Home if history is empty
    const handleBack = () => {
        if (isApp) {
            navigate('/');
            return;
        }

        // Check if the previous page was within our own domain
        const hasPreviousPage = document.referrer && document.referrer.includes(window.location.host);

        if (window.history.length > 1 && hasPreviousPage) {
            navigate(-1); // Go back in history if we navigated internally
        } else {
            navigate('/'); // Fallback to Home if we opened from an external shared link
        }
    };

    // Function to redirect to app store
    const redirectToAppStore = () => {
        if (Capacitor.getPlatform() === 'android') {
            window.open('market://details?id=com.pragathipublications.ainews', '_system'); // Replace with your actual Android Package Name
        } else if (Capacitor.getPlatform() === 'ios') {
            window.open('https://apps.apple.com/app/idYOUR_APP_ID', '_system'); // Replace with your actual iOS App ID
        } else {
            // Fallback for web, e.g., provide a link to a landing page with store links
            window.open('https://yourdomain.com/download-app', '_blank');
        }
    };

    return (
        <div className="single-post-page-container">
            {/* UPDATED: Uses the unified handleBack function */}
            <button className="back-button" onClick={handleBack}>
                &larr; {isApp ? 'Home' : 'Back'}
            </button>

            <div className="post-content">
                {loading && <div className="loader">Loading post...</div>}
                {error && <div className="error-message">{error}</div>}
                {newsItem && (
                    <>
                        <NewsCard
                            newsItem={newsItem}
                            isFeedView={true}
                            setActiveCommentPostId={setActiveCommentPostId} 
                        />
                        {!isApp && ( // Show app download recommendation only in browser
                            <div className="app-download-prompt">
                                <h3>🚀 Get the full experience!</h3>
                                <p>Download our app to post news, get personalized feeds, and more.</p>
                                <button onClick={redirectToAppStore} className="download-app-button">
                                    Download App
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default SinglePostPage;
